import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, ArrowLeft, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Import() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState("");

  const onFile = (f) => {
    setFile(f);
    setPreview(null);
    setError("");
  };

  const runPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/import/tests/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setCommitting(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/import/tests/commit?create_missing_orders=true",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `Imported ${data.created_tests} tests${
          data.created_orders ? `, created ${data.created_orders} new order(s)` : ""
        }`
      );
      navigate("/tests");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <Link
        to="/tests"
        data-testid="back-to-tests-from-import"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#50493b] hover:text-[#39678f] subtitle mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to registry
      </Link>

      <div className="mb-8">
        <div className="eyebrow mb-2">Bulk Import</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
          IMPORT LEGACY CSV
        </h1>
        <p className="font-body text-sm text-[#50493b] mt-3 max-w-2xl">
          Upload a SharePoint or Excel export. Jackstone will map columns (case-insensitive),
          resolve welder/tester names to employee records, flag unknown rows, and create
          any missing job numbers on commit.
        </p>
      </div>

      {/* Upload */}
      <div className="border border-[#33424f]/20 bg-white p-6 mb-6">
        <div className="eyebrow mb-3">/ 01 — Select file</div>
        <div
          className="border-2 border-dashed border-[#33424f]/30 bg-[#f5f1ec] p-8 text-center cursor-pointer hover:border-[#39678f] transition-colors"
          onClick={() => fileInputRef.current?.click()}
          data-testid="csv-dropzone"
        >
          <Upload className="w-8 h-8 text-[#39678f] mx-auto mb-3" />
          <div className="font-body text-sm text-[#33424f]">
            {file ? (
              <>
                <span className="subtitle">{file.name}</span> ·{" "}
                <span className="text-[#50493b]">{(file.size / 1024).toFixed(1)} KB</span>
              </>
            ) : (
              "Click to choose a CSV file"
            )}
          </div>
          <div className="text-xs text-[#50493b] mt-2 font-body">
            Expected columns: Job/Freezer Number, Vessel Type, Vessel Number, Test Result,
            Test Pressure, Test Medium, Test Date, Welder 1, Welder 2, Failure/Leak Point, Notes
          </div>
          <input
            ref={fileInputRef}
            data-testid="csv-file-input"
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button
            data-testid="preview-csv-btn"
            onClick={runPreview}
            disabled={!file || loading}
            className="bg-[#33424f] hover:bg-[#262f39] text-white subtitle uppercase tracking-wider text-xs h-10 disabled:opacity-50"
          >
            <FileText className="w-4 h-4 mr-2" />
            {loading ? "Parsing…" : "Preview import"}
          </Button>
          {file && (
            <Button
              variant="outline"
              onClick={() => {
                setFile(null);
                setPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="border-[#33424f]/30 text-[#33424f] subtitle uppercase tracking-wider text-xs h-10 hover:bg-[#f5f1ec]"
            >
              Clear
            </Button>
          )}
        </div>

        {error && (
          <div
            data-testid="import-error"
            className="mt-4 p-3 border border-[#b30035] bg-[#fbe4ea] text-[#7a0024] text-sm font-body"
          >
            {error}
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <>
          <div className="border border-[#33424f]/20 bg-white p-6 mb-6" data-testid="import-preview-summary">
            <div className="eyebrow mb-3">/ 02 — Preview</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Total rows" value={preview.summary.total_rows} />
              <Stat label="Valid" value={preview.summary.valid_rows} accent="text-[#2d5e1f]" />
              <Stat label="Errors" value={preview.summary.error_rows} accent="text-[#b30035]" />
              <Stat
                label="New orders"
                value={preview.summary.new_orders.length}
                accent="text-[#39678f]"
              />
            </div>
            {preview.summary.new_orders.length > 0 && (
              <div className="mt-4 text-xs font-body text-[#50493b]">
                <span className="subtitle text-[#33424f]">New orders to be created:</span>{" "}
                {preview.summary.new_orders.join(", ")}
              </div>
            )}
          </div>

          <div className="border border-[#33424f]/20 bg-white overflow-auto mb-6" data-testid="import-preview-rows">
            <table className="w-full data-table text-xs min-w-[900px]">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Line</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Job</th>
                  <th className="text-left px-3 py-2">Vessel</th>
                  <th className="text-left px-3 py-2">Result</th>
                  <th className="text-left px-3 py-2">Pressure</th>
                  <th className="text-left px-3 py-2">Medium</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">PTF</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr
                    key={r.line}
                    className={`border-t border-[#33424f]/10 ${
                      !r.ok ? "bg-[#fbe4ea]" : r.warnings.length ? "bg-[#fef9e2]" : ""
                    }`}
                  >
                    <td className="px-3 py-2 text-[#50493b]">{r.line}</td>
                    <td className="px-3 py-2">
                      {r.ok ? (
                        r.warnings.length ? (
                          <span className="inline-flex items-center gap-1 text-[#8a5a00] subtitle text-[10px] uppercase tracking-[0.15em]">
                            <AlertTriangle className="w-3 h-3" /> Warn
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#2d5e1f] subtitle text-[10px] uppercase tracking-[0.15em]">
                            <CheckCircle2 className="w-3 h-3" /> Ready
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#b30035] subtitle text-[10px] uppercase tracking-[0.15em]">
                          <AlertTriangle className="w-3 h-3" /> Error
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 subtitle text-[#33424f]">
                      {r.parsed.job_number || "—"}
                      {r.new_order && (
                        <span className="ml-1 text-[9px] subtitle uppercase tracking-[0.15em] text-[#39678f]">
                          · new
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.parsed.vessel_type || "—"}
                      {r.parsed.vessel_number ? ` #${r.parsed.vessel_number}` : ""}
                    </td>
                    <td className="px-3 py-2">{r.parsed.test_result || "—"}</td>
                    <td className="px-3 py-2">{r.parsed.test_pressure || "—"}</td>
                    <td className="px-3 py-2">{r.parsed.test_medium || "—"}</td>
                    <td className="px-3 py-2 text-[#50493b]">{r.parsed.test_date || "—"}</td>
                    <td className="px-3 py-2 mono subtitle text-[#33424f]">
                      {(r.parsed.failure_points || []).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-[#50493b] max-w-[200px] truncate">
                      {r.errors.length > 0 && (
                        <div className="text-[#b30035] subtitle text-[10px] uppercase tracking-[0.15em]">
                          {r.errors.join(", ")}
                        </div>
                      )}
                      {r.warnings.length > 0 && (
                        <div className="text-[#8a5a00] text-[10px]">
                          {r.warnings.join(", ")}
                        </div>
                      )}
                      {r.parsed.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border border-[#33424f]/20 bg-[#f5f1ec] p-4">
            <div className="text-sm text-[#50493b] font-body">
              Commit will create{" "}
              <span className="subtitle text-[#33424f]">{preview.summary.valid_rows}</span> test
              record{preview.summary.valid_rows !== 1 ? "s" : ""}
              {preview.summary.new_orders.length > 0 &&
                ` and ${preview.summary.new_orders.length} new order${preview.summary.new_orders.length !== 1 ? "s" : ""}`}
              .
            </div>
            <Button
              data-testid="commit-csv-btn"
              onClick={runCommit}
              disabled={committing || preview.summary.valid_rows === 0}
              className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-11 px-6 disabled:opacity-50"
            >
              {committing ? "Importing…" : `Commit ${preview.summary.valid_rows} records`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-[#33424f]" }) {
  return (
    <div>
      <div className="subtitle text-[10px] uppercase tracking-[0.2em] text-[#50493b]">{label}</div>
      <div className={`font-display text-4xl tabular-nums leading-none mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
